import DateTimeDisplay from '@app/components/common/DateTimeDisplay';
import { APP_PREFIX, VISITS_PAGE } from '@app/routes/urls';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import { BillingTransactionTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import type { BaseColumn } from '@konnecta/generic-table';
import { Typography } from 'antd';
import { TFunction } from 'i18next';
import type { CaregiverCashTxRow } from '../../types';

const getTransactionTypeLabel = (transactionType: string, t: TFunction): string => {
  switch (transactionType) {
    case BillingTransactionTypeEnum.CashCollection:
      return t('caregiver-cash-tx-type-collection');
    case BillingTransactionTypeEnum.CashDeposit:
      return t('caregiver-cash-tx-type-deposit');
    default:
      return transactionType;
  }
};

export const getCaregiverTransactionsTableColumns = (
  t: TFunction,
  locale: string
): BaseColumn<CaregiverCashTxRow>[] => [
  {
    id: 'transactionDate',
    header: t('caregiver-cash-tx-col-date'),
    accessor: 'transactionDate',
    width: 180,
    cellRenderer: (value) => (
      <DateTimeDisplay startTime={value as string} showDateSeparately={false} dateFormat="D MMM YYYY" timeFormat="" />
    ),
  },
  {
    id: 'transactionType',
    header: t('caregiver-cash-tx-col-type'),
    accessor: 'transactionType',
    width: 180,
    cellRenderer: (value) => <Typography>{getTransactionTypeLabel(value as string, t)}</Typography>,
  },
  {
    id: 'clientName',
    header: t('caregiver-cash-tx-col-client'),
    accessor: 'clientName',
    width: 200,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
  {
    id: 'visitId',
    header: t('caregiver-cash-tx-col-visit'),
    accessor: 'visitId',
    width: 120,
    cellRenderer: (value) =>
      value ? (
        <a
          href={`/${APP_PREFIX}/${VISITS_PAGE}/${value as number}/edit`}
          target="_blank"
          rel="noopener noreferrer"
          title={t('caregiver-cash-tx-open-visit')}
          style={{ color: '#1677ff', textDecoration: 'underline' }}
        >
          {`#${value as number}`}
        </a>
      ) : (
        <Typography>-</Typography>
      ),
  },
  {
    id: 'amount',
    header: t('caregiver-cash-tx-col-amount'),
    accessor: 'amount',
    align: 'right',
    width: 160,
    cellRenderer: (value, row) => (
      <div className="flex w-full justify-end">
        <Typography className="tabular-nums">{currencyFormatter(value as number, row.currency, locale)}</Typography>
      </div>
    ),
  },
  {
    id: 'notes',
    header: t('caregiver-cash-tx-col-note'),
    accessor: 'notes',
    width: 280,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
];
